import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Info,
  Loader2,
  Send,
  Sparkles,
  Trash2,
} from "lucide-react";
import ProductPreviewShell from "./ProductPreviewShell";
import {
  MAX_RESEARCH_MODELS,
  RESEARCH_MODELS,
  getResearchCard,
  modelLogoSrc,
} from "./modelConfig";
import {
  ComparativeAnalysisPanel,
  FinalSynthesisPanel,
} from "./ResearchAnalysisPanels";
import { PreviewAttachButton, PreviewEditExportButton, PreviewPanelActions, SectionCta } from "./PreviewUi";
import { useLandingPreviewEditor } from "./LandingPreviewEditorProvider";
import { researchModelEditorPayload } from "./previewEditorContent";
import StreamingStructuredBody from "./StreamingStructuredBody";
import { sectionsToTokens, useStructuredStream } from "./useMockStream";

const SAMPLE_QUERY =
  "Analyze the strongest arguments, key vulnerabilities, and missing facts in a breach-of-contract dispute involving late delivery and partial cure.";

const DEFAULT_SELECTED = ["chatgpt", "claude", "gemini", "perplexity"];

function ResponseCard({
  model,
  analysisRun,
  cardIndex,
  onStreamComplete,
  onDelete,
  canDelete,
  onEditExport,
}) {
  const card = getResearchCard(model.key);
  const tokens = useMemo(
    () => (card ? sectionsToTokens(card.sections) : []),
    [card],
  );
  const streaming = analysisRun > 0;
  const { tokens: streamedTokens, isStreaming, isDone } = useStructuredStream(
    tokens,
    streaming ? analysisRun : 0,
    {
      enabled: streaming,
      startDelay: cardIndex * 320,
      charDelay: 9,
    },
  );

  useEffect(() => {
    if (streaming && isDone) onStreamComplete();
  }, [streaming, isDone, onStreamComplete]);

  if (!card) return null;

  return (
    <div className="nl-response-card">
      <div
        className="nl-response-accent"
        style={{
          background: `linear-gradient(90deg, ${model.accent}, ${model.accent}88)`,
          transform: isStreaming || isDone ? "scaleX(1)" : "scaleX(0)",
        }}
      />
      <div className="nl-response-inner">
        <div className="nl-response-head">
          <span
            className="nl-response-logo-wrap"
            style={{
              background: `${model.accent}1a`,
              borderColor: `${model.accent}40`,
            }}
          >
            <img
              src={modelLogoSrc(model)}
              alt={model.name}
              className="nl-response-logo"
            />
          </span>
          <h4 className="nl-response-title" style={{ color: model.accent }}>
            {model.name}&apos;s Response Analysis
          </h4>
        </div>

        {streaming ? (
          <StreamingStructuredBody
            tokens={streamedTokens}
            isStreaming={isStreaming}
          />
        ) : (
          <div className="nl-response-html">
            {card.sections.map((section) => (
              <React.Fragment key={section.title}>
                <h3>{section.title}</h3>
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="nl-response-actions">
          <PreviewEditExportButton
            onClick={() => onEditExport(model.key)}
            disabled={isStreaming}
          />
          <div className="nl-response-actions-right">
            <span className="nl-response-status">
              {isStreaming ? (
                <>
                  <Loader2 size={14} className="nl-spin" /> Streaming…
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} /> Complete
                </>
              )}
            </span>
            <button
              type="button"
              className="nl-response-delete"
              aria-label={`Remove ${model.name} response`}
              title={canDelete ? "Remove response" : "At least one model must remain"}
              disabled={!canDelete}
              onClick={() => onDelete(model.key)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NewLandingResearchSection() {
  const navigate = useNavigate();
  const { openPreviewEditor } = useLandingPreviewEditor();
  const comparisonPanelRef = useRef(null);
  const synthesisPanelRef = useRef(null);
  const [selectedKeys, setSelectedKeys] = useState(DEFAULT_SELECTED);
  const [activeTab, setActiveTab] = useState("comparison");
  const [analysisRun, setAnalysisRun] = useState(0);
  const [cardsPending, setCardsPending] = useState(0);

  const toggleModel = (key) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.length > 1 ? prev.filter((k) => k !== key) : prev;
      }
      if (prev.length >= MAX_RESEARCH_MODELS) return prev;
      return [...prev, key];
    });
  };

  const selectedModels = useMemo(
    () => RESEARCH_MODELS.filter((m) => selectedKeys.includes(m.key)),
    [selectedKeys],
  );

  const analysisRunning = cardsPending > 0;

  const runAnalysis = () => {
    setCardsPending(selectedModels.length);
    setAnalysisRun((r) => r + 1);
    setActiveTab("comparison");
  };

  const onCardStreamComplete = useCallback(() => {
    setCardsPending((c) => Math.max(0, c - 1));
  }, []);

  const removeModel = useCallback(
    (key) => {
      setSelectedKeys((prev) => {
        if (prev.length <= 1 || !prev.includes(key)) return prev;
        return prev.filter((k) => k !== key);
      });
      if (analysisRunning) {
        setCardsPending((c) => Math.max(0, c - 1));
      }
    },
    [analysisRunning],
  );

  const handleModelEditExport = useCallback(
    (modelKey) => {
      const payload = researchModelEditorPayload(modelKey);
      if (payload) {
        openPreviewEditor(payload.title, payload.html);
      }
    },
    [openPreviewEditor],
  );

  const handlePanelEditExport = useCallback(() => {
    if (activeTab === "comparison") {
      openPreviewEditor(
        "Comparative Analysis",
        comparisonPanelRef.current?.innerHTML || "",
      );
      return;
    }
    openPreviewEditor(
      "Final Synthesis",
      synthesisPanelRef.current?.innerHTML || "",
    );
  }, [activeTab, openPreviewEditor]);

  return (
    <section className="research-section" id="research-preview">
      <div className="research-inner">
        <p className="section-label">Research Mode</p>
        <h2 className="section-title nl-research-title">
          How Multiple AI Opinions Become One Clear Strategy.
        </h2>
        <p className="section-sub">
          Ask a legal question or upload a brief. LawFusion compares multiple AI
          analyses, highlights disagreement, and synthesizes the strongest path
          forward.
        </p>

        <ProductPreviewShell>
          <div className="nl-research-app">
            <div className="nl-research-topbar">
              <div className="nl-research-tab-pill">
                <span className="nl-tp-dot" />
                Research Mode · Sample Preview
              </div>
              <div className="nl-research-topbar-right">
                <span className="nl-research-badge">
                  {analysisRunning ? (
                    <>
                      <Loader2 size={12} className="nl-spin" /> Analyzing…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={12} /> All {selectedKeys.length}{" "}
                      model{selectedKeys.length === 1 ? "" : "s"} responded
                    </>
                  )}
                </span>
              </div>
            </div>

            <div className="nl-research-shell">
              <div className="nl-research-query">
                <label className="nl-research-label">Sample Research Query</label>
                <div className="nl-research-textarea">{SAMPLE_QUERY}</div>
                <div className="nl-research-query-actions">
                  <PreviewAttachButton page="research" />
                  <button
                    type="button"
                    className={`nl-research-run${analysisRunning ? " is-running" : ""}`}
                    onClick={runAnalysis}
                    disabled={analysisRunning}
                  >
                    {analysisRunning ? (
                      <>
                        <Loader2 size={14} className="nl-spin" /> Running…
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Run Analysis
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="nl-model-picker">
                <div className="nl-model-picker-head">
                  <label className="nl-research-label">Select AI models</label>
                  <span className="nl-model-picker-count">
                    {selectedKeys.length}/{MAX_RESEARCH_MODELS} selected
                  </span>
                </div>
                <div className="nl-model-picker-grid">
                  {RESEARCH_MODELS.map((model) => {
                    const isOn = selectedKeys.includes(model.key);
                    const isCapped =
                      !isOn && selectedKeys.length >= MAX_RESEARCH_MODELS;
                    return (
                      <button
                        type="button"
                        key={model.key}
                        className={`nl-model-chip ${isOn ? "is-on" : ""} ${
                          isCapped ? "is-capped" : ""
                        }`}
                        onClick={() => toggleModel(model.key)}
                        disabled={isCapped || analysisRunning}
                        style={
                          isOn
                            ? {
                                borderColor: model.accent,
                                background: `${model.accent}14`,
                              }
                            : undefined
                        }
                      >
                        <span
                          className="nl-model-chip-check"
                          style={{
                            background: isOn ? model.accent : "transparent",
                            borderColor: isOn
                              ? model.accent
                              : "var(--nl-border-strong)",
                          }}
                        >
                          {isOn && (
                            <CheckCircle2
                              size={11}
                              strokeWidth={3}
                              color="#fff"
                            />
                          )}
                        </span>
                        <span
                          className="nl-model-chip-logo"
                          style={{
                            background: `${model.accent}1a`,
                            borderColor: `${model.accent}40`,
                          }}
                        >
                          <img src={modelLogoSrc(model)} alt={model.name} />
                        </span>
                        <span
                          className="nl-model-chip-name"
                          style={{ color: isOn ? model.accent : undefined }}
                        >
                          {model.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="nl-research-responses">
                {selectedModels.map((model, index) => (
                  <ResponseCard
                    key={model.key}
                    model={model}
                    analysisRun={analysisRun}
                    cardIndex={index}
                    onStreamComplete={onCardStreamComplete}
                    onDelete={removeModel}
                    canDelete={selectedKeys.length > 1}
                    onEditExport={handleModelEditExport}
                  />
                ))}
              </div>

              <div className="nl-tabs-list" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "comparison"}
                  className={`nl-tab-trigger ${
                    activeTab === "comparison" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveTab("comparison")}
                >
                  <span>Comparative Analysis</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "final"}
                  className={`nl-tab-trigger ${
                    activeTab === "final" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveTab("final")}
                >
                  <span>Final Synthesis</span>
                </button>
              </div>

              <div className="nl-tab-footer-block">
                {activeTab === "comparison" ? (
                  <div className="nl-tab-panel" role="tabpanel">
                    <div className="nl-panel-head">
                      <h3>Comparative Analysis</h3>
                      <div className="nl-panel-meta">
                        <span>
                          <Sparkles size={12} /> {selectedKeys.length} models
                          compared
                        </span>
                      </div>
                    </div>
                    <ComparativeAnalysisPanel ref={comparisonPanelRef} />
                  </div>
                ) : (
                  <div className="nl-tab-panel" role="tabpanel">
                    <div className="nl-panel-head">
                      <h3>Final Synthesis</h3>
                      <div className="nl-panel-meta">
                        <span className="nl-panel-strength">
                          Position:{" "}
                          <strong className="nl-strong-strong">STRONG</strong>
                        </span>
                      </div>
                    </div>
                    <FinalSynthesisPanel ref={synthesisPanelRef} />
                  </div>
                )}
                <PreviewPanelActions
                  page="research"
                  onEditExport={handlePanelEditExport}
                />
              </div>
            </div>
          </div>
        </ProductPreviewShell>

        <p className="preview-disclaimer">
          <span>
            <Info size={13} aria-hidden="true" />
            Sample output shown for illustration. Full results include more
            detailed analysis and vary by case and query.
          </span>
        </p>

        <SectionCta
          onClick={() => navigate("/research")}
          sub="No login required · Free analysis available"
        >
          Try Research Mode Free
        </SectionCta>
      </div>
    </section>
  );
}
